import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AuthHttpInterceptor } from '@auth0/auth0-angular';
import {
  COMPONENTS_BASE_API_URL,
  HOHR_COMPONENTS_TRANSLATIONS,
  HOHR_CONSENT_PATH,
  LoadingScreenModule,
  OnboardingService,
} from '@hohr/components';
import { ConfigModule, ConfigService } from '@hohr/config';
import {
  LAUNCHER_BASE_API_URL,
  PortalLauncherModule,
} from '@hohr/portal-launcher';
import { IonicModule } from '@ionic/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_PROVIDERS } from './app.init';
import { AppConfigService } from './core/config/config.service';
import { CoreModule } from './core/core.module';
import { API_BASE_URL } from './core/data-access/api';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { RegistrationInterceptor } from './core/interceptors/registration.interceptor';
import { CustomOnboardingService } from './core/services/custom-onboarding.service';
import { CustomTranslationHttpLoader } from './core/services/custom-translation-http-loader.service';
import { InternationalizationService } from './core/services/internationalization.service';
import { SharedModule } from './shared/shared.module';
import { apimSubscriptionInterceptor } from '@app/core/interceptors/apim-subscription.interceptor';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    CoreModule,
    IonicModule.forRoot(),
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (
          internationalizationService: InternationalizationService
        ) => new CustomTranslationHttpLoader(internationalizationService),
        deps: [InternationalizationService],
      },
    }),
    BrowserAnimationsModule,
    PortalLauncherModule,
    ConfigModule,
    LoadingScreenModule,
  ],
  providers: [
    APP_PROVIDERS,
    {
      provide: API_BASE_URL,
      deps: [AppConfigService],
      useFactory: (config: AppConfigService) => {
        return config.baseApiUrl;
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        floatLabel: 'always',
        appearance: 'outline',
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'nl-be',
    },
    {
      provide: LAUNCHER_BASE_API_URL,
      deps: [AppConfigService],
      useFactory: (config: AppConfigService) => {
        return config.baseApiUrl;
      },
    },
    {
      provide: COMPONENTS_BASE_API_URL,
      deps: [AppConfigService],
      useFactory: (config: AppConfigService) => {
        return `${config.baseApiUrl}`;
      },
    },
    {
      provide: HOHR_COMPONENTS_TRANSLATIONS,
      useFactory: (service: ConfigService) => {
        return new BehaviorSubject<Record<string, string>>(
          service.hohrSharedTranslations
        );
      },
      deps: [ConfigService],
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([apimSubscriptionInterceptor])
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RegistrationInterceptor,
      multi: true,
    },
    {
      provide: HOHR_CONSENT_PATH,
      useValue: 'consent',
    },
    { provide: OnboardingService, useClass: CustomOnboardingService },
  ],
  bootstrap: [AppComponent],
  exports: [RouterModule],
})
export class AppModule {}
